// src/redux/slices/activeShipmentSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getUserData } from "../../utils/authUtils";
import { API_BASE_URL } from "../../config";
import type { RootState } from "../store"; // <-- adjust path if needed

interface Shipment {
  shipment_id: string;
  shipment_status: string;
  [key: string]: any;
}

interface ShipmentState {
  shipments: Shipment[];
  loading: boolean;
  error: string | null;
  deleteLoading: boolean;
  deleteError: string | null;

  // ✅ dedupe maps (per key)
  loadingByKey: Record<string, boolean>;
  loadedByKey: Record<string, boolean>;
  lastFetchedAtByKey: Record<string, number>;
}

const initialState: ShipmentState = {
  shipments: [],
  loading: false,
  error: null,
  deleteLoading: false,
  deleteError: null,

  loadingByKey: {},
  loadedByKey: {},
  lastFetchedAtByKey: {},
};

// Build a stable key for dedupe (status + next_id + user_id)
const keyForStatus = (
  status: string | undefined,
  nextId: string,
  userId: string,
) => `status:${status ?? ""}|next:${nextId}|user:${userId}`;

// Single key for charts fetch (aggregated)
const CHARTS_KEY = "charts_all";

type FetchAllArg = string | { shipment_status?: string; force?: boolean };

const normalizeFetchAllArg = (arg: FetchAllArg) =>
  typeof arg === "string"
    ? { shipment_status: arg, force: false }
    : { shipment_status: arg?.shipment_status, force: !!arg?.force };

// -------------------- fetchAllShipments (deduped) --------------------
export const fetchAllShipments = createAsyncThunk<
  Shipment[],
  FetchAllArg | void,
  { state: RootState; rejectValue: string }
>(
  "shipments/fetchAll",
  async (arg, { rejectWithValue }) => {
    try {
      const { shipment_status } = normalizeFetchAllArg(arg ?? {});
      const userData = getUserData();
      const token = userData?.Customerdetail?.token;
      const user_id = userData?.Customerdetail?.id;
      const next_id = sessionStorage.getItem("next_id") || "0";

      const formData = new FormData();
      formData.append("shipment_id", "");
      formData.append("sender_company_name", "");
      formData.append("receiver_company_name", "");
      formData.append("origin", "");
      formData.append("destination", "");
      formData.append("sender_areaname", "");
      formData.append("receiver_areaname", "");
      formData.append("sender_gst_no", "");
      formData.append("shipment_status", shipment_status || "");
      formData.append("receiver_gst_no", "");
      formData.append("user_id", user_id);
      formData.append("next_id", next_id);

      const response = await axios.post(
        `${API_BASE_URL}/shipmentactivelist`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const shipmentData = response?.data?.shipment?.[0]?.shipmentData;
      return Array.isArray(shipmentData) ? shipmentData : [];
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  },
  {
    // ✅ Dedupe by (status,next_id,user_id)
    condition: (arg, { getState }) => {
      const state = getState() as RootState;
      const s = state.activeShipment as ShipmentState;

      const { shipment_status, force } = normalizeFetchAllArg(arg ?? {});
      const userData = getUserData();
      const user_id = userData?.Customerdetail?.id || "";
      const next_id = sessionStorage.getItem("next_id") || "0";
      const key = keyForStatus(shipment_status, next_id, user_id);

      if (force) return true;
      if (s.loadingByKey[key]) return false;
      if (s.loadedByKey[key]) {
        // Optional TTL
        // const ttlMs = 120_000;
        // const last = s.lastFetchedAtByKey[key] || 0;
        // if (Date.now() - last > ttlMs) return true;
        return false;
      }
      return true;
    },
  },
);

// -------------------- fetchAllShipmentsForCharts (deduped) --------------------
export const fetchAllShipmentsForCharts = createAsyncThunk<
  Shipment[],
  { force?: boolean } | void,
  { state: RootState; rejectValue: string }
>(
  "shipments/fetchAllForCharts",
  async (_arg, { rejectWithValue }) => {
    try {
      const userData = getUserData();
      const token = userData?.Customerdetail?.token;
      const user_id = userData?.Customerdetail?.id;
      const next_id = sessionStorage.getItem("next_id") || "0";

      const statusesToTry = [
        "",
        "all",
        "pending",
        "approved",
        "hold",
        "delivered",
        "cancelled",
      ];

      let allShipments: any[] = [];
      let bestResult: any[] = [];

      for (const status of statusesToTry) {
        try {
          const formData = new FormData();
          formData.append("shipment_id", "");
          formData.append("sender_company_name", "");
          formData.append("receiver_company_name", "");
          formData.append("origin", "");
          formData.append("destination", "");
          formData.append("sender_areaname", "");
          formData.append("receiver_areaname", "");
          formData.append("sender_gst_no", "");
          formData.append("shipment_status", status);
          formData.append("receiver_gst_no", "");
          formData.append("user_id", user_id);
          formData.append("next_id", next_id);

          const response = await axios.post(
            `${API_BASE_URL}/shipmentactivelist`,
            formData,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          const shipmentData = response?.data?.shipment?.[0]?.shipmentData;
          if (Array.isArray(shipmentData) && shipmentData.length > 0) {
            if (shipmentData.length > bestResult.length)
              bestResult = shipmentData;
            if (
              (status === "" || status === "all") &&
              shipmentData.length > 0
            ) {
              allShipments = shipmentData;
              break;
            }
          }
        } catch (err) {
          console.warn(`Failed to fetch with status "${status}":`, err);
        }
      }

      const finalResult = allShipments.length > 0 ? allShipments : bestResult;
      return finalResult;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  },
  {
    // ✅ Dedupe by single charts key
    condition: (arg, { getState }) => {
      const state = getState() as RootState;
      const s = state.activeShipment as ShipmentState;

      const force = !!(arg as any)?.force;
      if (force) return true;

      if (s.loadingByKey[CHARTS_KEY]) return false;
      if (s.loadedByKey[CHARTS_KEY]) {
        // Optional TTL
        // const ttlMs = 120_000;
        // const last = s.lastFetchedAtByKey[CHARTS_KEY] || 0;
        // if (Date.now() - last > ttlMs) return true;
        return false;
      }
      return true;
    },
  },
);

// -------------------- deleteShipment (unchanged) --------------------
export const deleteShipment = createAsyncThunk(
  "shipments/delete",
  async (shipment_id: string, { rejectWithValue }) => {
    try {
      const userData = getUserData();
      const token = userData?.Customerdetail?.token;
      const formData = new FormData();
      formData.append("shipment_id", shipment_id);

      const response = await axios.post(
        "https://my.axlpl.com/messenger/services_v7/delete_shipment",
        formData,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      return { shipment_id, response: response.data };
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  },
);

const activeShipmentSlice = createSlice({
  name: "activeShipment",
  initialState,
  reducers: {
    // optional manual reset
    resetShipments(state) {
      state.shipments = [];
      state.loading = false;
      state.error = null;
      state.deleteLoading = false;
      state.deleteError = null;
      state.loadingByKey = {};
      state.loadedByKey = {};
      state.lastFetchedAtByKey = {};
    },
  },
  extraReducers: (builder) => {
    // fetchAllShipments
    builder.addCase(fetchAllShipments.pending, (state, action) => {
      const { shipment_status } = normalizeFetchAllArg(action.meta.arg ?? {});
      const userData = getUserData();
      const user_id = userData?.Customerdetail?.id || "";
      const next_id = sessionStorage.getItem("next_id") || "0";
      const key = keyForStatus(shipment_status, next_id, user_id);

      state.loading = true;
      state.error = null;
      state.loadingByKey[key] = true;
      // TIP: Do NOT clear data here to avoid flicker
      // state.shipments = [];
    });
    builder.addCase(fetchAllShipments.fulfilled, (state, action) => {
      // Mark key done
      const { shipment_status } = normalizeFetchAllArg(action.meta.arg ?? {});
      const userData = getUserData();
      const user_id = userData?.Customerdetail?.id || "";
      const next_id = sessionStorage.getItem("next_id") || "0";
      const key = keyForStatus(shipment_status, next_id, user_id);

      state.loading = false;
      state.shipments = action.payload;
      state.loadingByKey[key] = false;
      state.loadedByKey[key] = true;
      state.lastFetchedAtByKey[key] = Date.now();
    });
    builder.addCase(fetchAllShipments.rejected, (state, action) => {
      const { shipment_status } = normalizeFetchAllArg(action.meta.arg ?? {});
      const userData = getUserData();
      const user_id = userData?.Customerdetail?.id || "";
      const next_id = sessionStorage.getItem("next_id") || "0";
      const key = keyForStatus(shipment_status, next_id, user_id);

      state.loading = false;
      state.error = action.payload as string;
      state.loadingByKey[key] = false;
    });

    // fetchAllShipmentsForCharts
    builder.addCase(fetchAllShipmentsForCharts.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.loadingByKey[CHARTS_KEY] = true;
    });
    builder.addCase(fetchAllShipmentsForCharts.fulfilled, (state, action) => {
      state.loading = false;
      state.shipments = action.payload;
      state.loadingByKey[CHARTS_KEY] = false;
      state.loadedByKey[CHARTS_KEY] = true;
      state.lastFetchedAtByKey[CHARTS_KEY] = Date.now();
    });
    builder.addCase(fetchAllShipmentsForCharts.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      state.loadingByKey[CHARTS_KEY] = false;
    });

    // delete
    builder.addCase(deleteShipment.pending, (state) => {
      state.deleteLoading = true;
      state.deleteError = null;
    });
    builder.addCase(deleteShipment.fulfilled, (state, action) => {
      state.deleteLoading = false;
      state.shipments = state.shipments.filter(
        (s) => s.shipment_id !== action.payload.shipment_id,
      );
    });
    builder.addCase(deleteShipment.rejected, (state, action) => {
      state.deleteLoading = false;
      state.deleteError = action.payload as string;
    });
  },
});

export const { resetShipments } = activeShipmentSlice.actions;
export default activeShipmentSlice.reducer;
